/**
 * Resource GraphQL Fragments
 *
 * Reusable fragments for Resource primitive queries and mutations.
 * Aligned with graphql_gateway/src/resource_api/resource_model.rs GqlResource fields.
 *
 * @see graphql_gateway/src/resource_api/resource_model.rs - Backend model
 */

import { gql } from '@apollo/client';

/**
 * Complete Resource fields fragment
 *
 * Used by all queries and mutations that return Resource data.
 * Excludes #[graphql(skip)] fields (tenant_id, formula interaction IDs,
 * owner_actor_id, allowed_actor_ids, created_by, updated_by).
 */
export const RESOURCE_FIELDS = gql`
  fragment ResourceFields on Resource {
    # Identity & Context
    resourceId
    resourceName
    resourceCode
    slug
    parentResourceId
    correlationId
    tags

    # Resource Configuration
    resourceType
    resourceCategory
    resourceSubcategory
    unitOfMeasure
    description
    manufacturer
    modelNumber
    serialNumber
    barcode
    locationId
    costCenter
    ownerId
    assetTag
    isPhysical

    # Capacity Management
    totalCapacity
    availableCapacity
    reservedCapacity
    inUseCapacity
    minimumReserve
    currentQuantity
    reorderThreshold
    isConsumable
    reservationEnabled
    concurrentUsageAllowed
    maxConcurrentUsers
    isLifeCriticalResource

    # Allocation & Booking
    bookingWindowDays
    minBookingDurationMinutes
    maxBookingDurationMinutes
    allowOverbooking
    conflictResolution
    autoReleaseMinutes

    # Status & Lifecycle
    resourceStatus
    lifecycleStage
    lifecycleStartDate
    lastMaintenanceDate
    nextMaintenanceDate
    maintenanceIntervalDays
    warrantyExpiryDate
    retirementDate
    decommissionDate

    # Performance & Monitoring
    totalUsageCount
    utilizationRatePercent
    idleTimePercent
    peakUsageHour
    peakUsageDay
    anomalyScore
    avgUtilizationPercent
    peakUtilizationPercent
    efficiencyRating
    totalDowntimeHours
    lastUsedAt
    usageTrend

    # Financial & Compliance
    acquisitionCost
    currentValue
    depreciationMethod
    depreciationRate
    monthlyCost
    totalCostToDate
    complianceStatus
    lastAuditDate
    certifications

    # Life-Critical Features
    sterilizationRequired
    sterilizationCycleCount
    lastSterilizationDate

    # Access Control
    restrictedToRoles
    requiresCertification
    requiresApprovalForUse
    accessAuditEnabled

    # Lifecycle & Audit
    createdAt
    updatedAt
    archivedAt
    archivedReason

    # Append-Only
    originalId
    parentId
    isActive

    # Extended
    extendedMetadata

    # Computed (ComplexObject resolvers)
    compressedId
    ownerActor {
      actorId
      displayName
    }
    createdByActor {
      actorId
      displayName
    }
    updatedByActor {
      actorId
      displayName
    }
  }
`;

/**
 * Resource connection fields (pagination)
 */
export const RESOURCE_CONNECTION_FIELDS = gql`
  ${RESOURCE_FIELDS}
  fragment ResourceConnectionFields on ResourceConnection {
    items {
      ...ResourceFields
    }
    total
    nextCursor
    hasMore
  }
`;

/**
 * Resource utilization report fields
 */
export const RESOURCE_UTILIZATION_REPORT_FIELDS = gql`
  fragment ResourceUtilizationReportFields on ResourceUtilizationReport {
    resourceId
    utilizationPercent
    idlePercent
    peakHour
    totalUsageCount
    reportGeneratedAt
  }
`;
